import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { IconGraphy, Table, Tooltip, Button } from '@leapeasy/ui-kit';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { InviteTypeBadge, RequestStatusBadge, RoleBadge } from './notificationTableRenderers';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';
import { buildMaskedNotificationMessageRow } from 'utils/demoMask';

export const NotificationFromTable = forwardRef(
  (
    {
      messages,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickMessage,
      onClickAccept,
      onClickDecline,
      onClickRow,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
    },
    ref,
  ) => {
    const { isDemo } = useDemoMask();
    const [tableData, setTableData] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    useEffect(() => {
      if (!messages) return;
      const sorted = [...messages].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      setTableData(sorted.map((message) => buildMaskedNotificationMessageRow(message, isDemo)));
    }, [messages, isDemo]);

    const findMessage = (tableMeta) => {
      const id = tableMeta[tableMeta.length - 2];
      return messages.find((message) => message.id === id);
    };

    const goDetailPage = (tableMeta) => {
      const selectedMessage = findMessage(tableMeta);
      if (selectedMessage) onClickMessage(selectedMessage);
    };

    return (
      <React.Fragment>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No received notifications match your search.'
                  : 'No received notifications available yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((message) =>
                    buildMaskedNotificationMessageRow(message, isDemo),
                  );
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((message) =>
                    buildMaskedNotificationMessageRow(message, isDemo),
                  );
                }
              : undefined
          }
          data={tableData}
          autoTooltipForDefaultCells
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          onClickRow={
            onClickRow
              ? (row) => {
                  const message = row?.[row.length - 1];
                  if (message?.id) onClickRow(message);
                }
              : undefined
          }
          style={{ width: '100%' }}
          title="Received notifications"
          columns={[
            {
              name: 'Request Type',
              key: 'request_type',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <InviteTypeBadge value={value} />,
              },
            },
            {
              name: 'Status',
              key: 'request_status',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <RequestStatusBadge value={value} />,
              },
            },
            {
              name: 'From Email',
              key: 'requestor_email',
              options: { sort: true },
            },
            {
              name: 'From Role',
              key: 'requestor_role',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <RoleBadge role={value} />,
              },
            },
            {
              name: 'To Email',
              key: 'accepter_email',
              options: { sort: true },
            },
            {
              name: 'To Role',
              key: 'accepter_role',
              options: {
                flex: '40px 1 1',
                sort: true,
                customBodyRenderer: (value) => <RoleBadge role={value} textSize="small" />,
              },
            },
            {
              name: 'Property Count',
              key: 'property.length',
              options: { sort: true },
            },
            {
              name: 'Date',
              key: 'updatedAt',
              options: { sort: true },
            },
            {
              name: 'Detail',
              key: 'detail',
              options: {
                sort: false,
                flex: '5px 1 1',
                customBodyRenderer: (_value, tableMeta) => (
                  <Tooltip title="View notification details" placement="top" showCarot>
                    <div>
                      <IconGraphy
                        icon="FileFolder.Description"
                        style={{ color: '#702572', margin: 'auto', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          goDetailPage(tableMeta);
                        }}
                      />
                    </div>
                  </Tooltip>
                ),
              },
            },
            {
              name: '',
              key: 'actions',
              options: {
                sort: false,
                flex: '120px 1 1',
                customBodyRenderer: (_value, tableMeta) => {
                  const selectedMessage = findMessage(tableMeta);
                  if (selectedMessage?.request_status !== 'pending') return null;
                  return (
                    <ActionRow>
                      <Button
                        variant="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickAccept?.(selectedMessage);
                        }}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClickDecline?.(selectedMessage);
                        }}
                      >
                        Decline
                      </Button>
                    </ActionRow>
                  );
                },
              },
            },
          ]}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Received notifications"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </React.Fragment>
    );
  },
);

NotificationFromTable.displayName = 'NotificationFromTable';

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;
